import { memo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Clapperboard, X } from "lucide-react";
import type { PresencePayload } from "@wedding/shared";
import { cn } from "../../lib/utils";

/** Shared viewer list (join order, role chips) — used by the mobile bottom
 *  sheet AND the xl docked audience rail. */
export function RejiPresenceList({ presence }: { presence: PresencePayload | null }) {
  const { t } = useTranslation();
  const viewers = presence?.viewers ?? [];
  const total = presence?.total ?? 0;

  return (
    <ul className="overflow-y-auto py-2">
      {viewers.length === 0 && (
        <li className="px-5 py-3 text-white/40 text-sm">{t("slideshowRemote.offline")}</li>
      )}
      {viewers.map((v) => (
        <li key={v.id} className="flex items-center gap-3 px-5 py-2.5">
          <span
            className={cn(
              "size-9 rounded-full flex items-center justify-center font-bold text-sm border select-none shrink-0",
              v.role === "reji"
                ? "bg-primary/25 border-primary/40 text-primary"
                : "bg-white/10 border-white/20 text-white/85",
            )}
          >
            {v.name?.[0]?.toUpperCase() ?? "?"}
          </span>
          <span className="text-white/85 text-sm truncate">
            {v.name || t("reji.roleGuest")}
          </span>
          <span
            className={cn(
              "ml-auto shrink-0 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5",
              v.role === "reji"
                ? "text-primary bg-primary/15 border border-primary/30"
                : "text-white/45 bg-white/5 border border-white/10",
            )}
          >
            {v.role === "reji" && <Clapperboard className="h-3 w-3" />}
            {v.role === "reji" ? t("reji.roleReji") : t("reji.roleGuest")}
          </span>
        </li>
      ))}
      {total > viewers.length && (
        <li className="px-5 py-2.5 text-white/45 text-xs">
          {t("slideshow.presenceMore", { count: total - viewers.length })}
        </li>
      )}
    </ul>
  );
}

/** Shared header for both presentations. */
export function RejiPresenceHeader({
  presence,
  onClose,
}: {
  presence: PresencePayload | null;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
      <p className="text-white/90 text-sm font-semibold uppercase tracking-[0.22em]">
        {t("reji.presenceSheetTitle")}
        <span className="ml-2 text-white/45 tabular-nums normal-case tracking-normal">
          {t("slideshowRemote.viewers", { count: presence?.total ?? 0 })}
        </span>
      </p>
      {onClose && (
        <button
          onClick={onClose}
          aria-label={t("reji.exitWatch")}
          className="text-white/55 hover:text-white transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

interface RejiPresenceSheetProps {
  presence: PresencePayload | null;
  open: boolean;
  onClose: () => void;
}

/** Bottom sheet presentation (mobile / <xl — the xl rail is always docked). */
function RejiPresenceSheetImpl({ presence, open, onClose }: RejiPresenceSheetProps) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-[#1b1817]/95 backdrop-blur-xl border-t border-white/10 max-h-[70vh] flex flex-col safe-bottom">
        <RejiPresenceHeader presence={presence} onClose={onClose} />
        <RejiPresenceList presence={presence} />
      </div>
    </div>,
    document.body,
  );
}

export const RejiPresenceSheet = memo(RejiPresenceSheetImpl);
